import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Box, Typography, Stack, Button } from "@mui/material";

const insights = [
  {
    number: "1.",
    text: `<strong>Genetic testing has become a commodity.</strong> Genetic testing is becoming ubiquitous, genetic testing identifies carriers by traditional genetic counseling, and mainstreaming (Point of care testing by physicians and other providers, somatic testing, population testing, direct to consumer testing, etc.).  All these processes are being enhanced by the use of AI.  We are moving away from measuring quality by the number of tests done.  Today, widespread testing is expected as a given.`,
  },
  {
    number: "2.",
    text: `Identifying patients eligible for genetic testing remains complex AI algorithms enable accurate risk assessment and facilitate the testing process.`,
  },
  {
    number: "3.",
    text: `<strong>Many more carriers are being found.</strong> We have moved toward measuring quality by the number of carriers found and managed properly. The new challenge is providing individualized management to every patient who carries a pathogenic variant. Our <a href="https://hereditarycancer.ai/hereditary-cancer-clinic" target="_blank" rel="noopener noreferrer" style="color: #1B2E85; text-decoration: underline;">Hereditary Cancer Clinic App</a> provides advanced genetic insights that enable personalized management strategies for hereditary cancer, minimizing morbidity and mortality.`,
  },
  {
    number: "4.",
    text: `<strong>Our knowledge of cancer genetics has expanded beyond what the unaided human brain can manage.</strong> Cancer genetics now includes many, many genes.  Each gene has its own spectrum, penetrance and age of onset. As the human brain approaches its limit, targeted and searchable access to educational information in multiple formats is essential.  We will provide this as video, text, audio, visualizations (graphs), chatbots, and whatever other means we can leverage. Integrating AI with real-time patient data to refine and adapt clinical strategies, ensuring each patient receives the most effective, personalized care plan over time.`,
  },
  {
    number: "5.",
    text: `<strong>Management of patients with pathogenic variants is complex and changes frequently.</strong> Clinical Decision Support (CDS) tools are becoming essential to manage the plethora of genes and guidelines.  Utilizing AI, CDS offers precision treatment planning based on individual genetic profiles. This targeted approach enhances the efficacy of screening and prevention, preventing cancer or finding it at an earlier and more treatable stage.  When cancer does occur, targeted treatments specific to the gene increase efficacy while reducing side effects. Continuous monitoring of hereditary cancer patients includes proactive interventions to improve patient outcomes.`,
  },
  {
    number: "6.",
    text: `Through AI-driven research, education and innovation, we strive to constantly improve our understanding of hereditary cancer, leading to the development of more effective management strategies and therapies.`,
  },
];

type InsightItemProps = {
  number: string;
  text: string;
  index: number;
};

const InsightItem: React.FC<InsightItemProps> = ({ number, text, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.15,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "#1B2E85", mb: 1 }}
        >
          {number}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: "1.15rem", lineHeight: 1.8 }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      </Box>
    </motion.div>
  );
};

export const HeroImage = () => {
  return (
    <>
      <Box
        sx={{
          height: { xs: "180px", md: "250px" },
          width: "100%",
          backgroundImage: "url('/why-us-hero.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          pt: "80px",
        }}
      />
      <Box sx={{ width: "100%", height: "20px", backgroundColor: "#1B2E85" }} />
    </>
  );
};

export const HeroContentBox: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        maxWidth: "560px",
        marginTop: "-120px",
        position: "relative",
        padding: { xs: 3, md: 4 },
        mx: "auto",
        textAlign: "center",
      }}
    >
      <Typography
        variant="h2"
        fontWeight="bold"
        textAlign="center"
        color="#1B2E85"
        sx={{ mb: 2 }}
      >
        Why AI in Cancer Genetics?
      </Typography>
    </Box>
  );
};

export const AICancerGeneticsPage = () => {
  return (
    <Box sx={{ maxWidth: 800, mx: "auto", mt: 8, px: 2, pb: 12 }}>
      <Stack spacing={6}>
        {insights.map((item, idx) => (
          <InsightItem
            key={idx}
            number={item.number}
            text={item.text}
            index={idx}
          />
        ))}

        {/* Return Home Button */}
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Button
            href="/"
            variant="outlined"
            sx={{
              color: "#1B2E85",
              borderColor: "#1B2E85",
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
              py: 1,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#1B2E85",
                color: "white",
                borderColor: "#1B2E85",
              },
            }}
          >
            Return to Home
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
